import { useEffect, useState } from "react";
import { Star, Quote, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import Reveal from "@/components/Reveal";
import { useSettings } from "@/lib/settings";
import { useContent, Em } from "@/lib/content";

function Stars({ n }) {
  return (
    <span className="flex gap-0.5" aria-label={`${n} étoiles sur 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={14} className={i <= n ? "fill-teal text-teal" : "text-line"} />
      ))}
    </span>
  );
}

export default function Testimonials() {
  const settings = useSettings();
  const { t } = useContent();
  const [all, setAll] = useState([]);
  useEffect(() => {
    api.get("/testimonials?limit=100").then((r) => setAll(r.data)).catch(() => {});
  }, []);
  if (all.length === 0) return null;

  const items = all.slice(0, 6);
  const avg = (all.reduce((s, t) => s + (t.rating || 5), 0) / all.length).toFixed(1);

  return (
    <section data-testid="testimonials-section" className="py-20 md:py-28 bg-cream relative overflow-hidden">
      <span aria-hidden className="absolute -top-20 -left-20 h-72 w-72 rounded-full bg-mint/25 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8">
        <Reveal className="lg:col-span-4">
          <p className="eyebrow mb-4">{t("testi.eyebrow")}</p>
          <h2 className="font-serif text-4xl md:text-5xl text-teal-dark leading-[1.05]">
            <Em>{t("testi.title")}</Em>
          </h2>
          <div className="mt-8 inline-flex items-center gap-4 rounded-3xl bg-white border border-line px-5 py-4">
            <p className="font-serif text-5xl text-teal leading-none" data-testid="testimonials-average">{avg}</p>
            <div>
              <Stars n={Math.round(Number(avg))} />
              <p className="mt-1 text-xs text-ink/60">{t("testi.count_label", { n: String(all.length) })}</p>
            </div>
          </div>
          {settings.google_reviews_url && (
            <a
              data-testid="testimonials-google-link"
              href={settings.google_reviews_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-teal link-underline"
            >
              {t("testi.google_link")} <ArrowUpRight size={15} />
            </a>
          )}
        </Reveal>

        <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          {items.map((t, i) => (
            <Reveal key={t.id} delay={i * 0.07} className={i % 3 === 0 ? "md:translate-y-6" : ""}>
              <figure data-testid={`testimonial-${t.id}`} className="h-full rounded-3xl bg-white border border-line p-6 md:p-7 flex flex-col">
                <Quote size={26} className="text-rose fill-rose/60" />
                <blockquote className="mt-4 text-ink/85 leading-relaxed flex-1">« {t.text} »</blockquote>
                <figcaption className="mt-6 flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-teal-dark">{t.name}</p>
                    <p className="text-xs text-mauve">{[t.zone, t.source === "google" ? "Avis Google" : "Via WhatsApp"].filter(Boolean).join(" • ")}</p>
                  </div>
                  <Stars n={t.rating || 5} />
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
